import { BlitzPage } from "@blitzjs/next"
import { useRouter } from "next/router"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { Alert, Box, Divider, Grid } from "@mui/material"
import { useTranslation } from "next-i18next"
import Layout from "src/app/core/layouts/Layout"
import { useOAuthFlow } from "src/app/auth/hooks/useOAuthFlow"
import MyLoginForm from "src/app/auth/components/MyLoginForm"
import { useSchoolConfig } from "src/app/shop/shared/hooks/useSchoolConfig"
import LoginWithSmartschoolButton from "src/app/shop/feature-login/components/LoginWithSmartschoolButton"
import LoginCodeForm from "src/app/shop/feature-login/components/LoginCodeForm"
import LoginPaper from "src/app/shop/feature-login/components/LoginPaper"
import SchoolLogo from "src/app/shop/feature-login/components/SchoolLogo"
import { Footer } from "src/app/core/components/Footer"

const LoginPage: BlitzPage = () => {
  const { query } = useRouter()
  const { t } = useTranslation("shop")
  const schoolSlug = query.school as string | undefined
  const schoolConfig = useSchoolConfig(schoolSlug)
  const isOAuthCallback = useOAuthFlow()

  if (isOAuthCallback) return null

  if (!schoolSlug) {
    return <MyLoginForm loginType="emailCode" />
  }

  const showSmartschool = Boolean(schoolConfig?.useSmartschool)
  const showLoginCode = Boolean(schoolConfig?.useLoginCodes)
  const authError = (query.authError as string)?.replace("Error: ", "")

  return (
    <Box
      sx={{ backgroundColor: "background.alt" }}
      display="flex"
      flexDirection="column"
      height="100%"
    >
      <Box padding={2} width="100%" display="flex" justifyContent="space-between">
        <SchoolLogo schoolSlug={schoolSlug} />
      </Box>
      <LoginPaper>
        <Grid
          maxWidth={500}
          container
          flexDirection="column"
          justifyContent="center"
          alignContent="center"
          spacing={2}
        >
          {showSmartschool && (
            <Grid item style={{ textAlign: "center" }}>
              <LoginWithSmartschoolButton />
            </Grid>
          )}
          {showSmartschool && showLoginCode && (
            <Grid item>
              <Divider>{t("authOrDivider")}</Divider>
            </Grid>
          )}
          {showLoginCode && (
            <Grid item>
              <LoginCodeForm schoolSlug={schoolSlug} />
            </Grid>
          )}
          {authError && (
            <Grid item>
              <Alert severity="error">
                {t("authError", {
                  context: authError,
                  defaultValue: t("authError", { context: "generic" }),
                })}
              </Alert>
            </Grid>
          )}
        </Grid>
      </LoginPaper>
      <Footer />
    </Box>
  )
}

LoginPage.redirectAuthenticatedTo = (context) => {
  if (context.session.schoolSlug) return `/${context.session.schoolSlug}`
  return false
}
LoginPage.getLayout = (page) => <Layout title="Log In">{page}</Layout>

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["common", "shop"])),
    },
  }
}

export default LoginPage
